// ./src/components/features/EventPartnerPackages/EventPartnerPackagesList/EventPartnerPackagesList.tsx
//
// The offerings an event makes: which catalog packages are on the table, at what price, how many
// slots, and whether applications are open.
//
// ⚠ TWO SEPARATE SIGNALS, shown separately. "Live / Draft" (status) is whether the offering is
// listed at all; "Open / Shut" (isOpenForApplications) is whether it is taking applications right
// now. A host wants a sold-out Gold tier still visible with applications shut, so collapsing these
// into one badge would force them to delete the offering to stop the flow.
//
// Presentation follows the house rule (em-scaffold-ui → components):
//   desktop (≥ md) → a real <Table>, actions always visible
//   tablet (sm–md) → cards, actions always visible
//   phone  (< sm)  → cards in a <SwipeableCard>, NO visible buttons — the swipe IS the actions
// ⚠ ONE branch is mounted at a time, via useAppEnvironment (a live media query), never two hidden
// by CSS: a phone card that also renders the buttons shows them twice, once inline and once under
// the swipe.
import React from "react";
import { useTranslation } from "react-i18next";
import { Table, Paper, Stack, Group, Text, Badge, Box, ActionIcon, Tooltip, Skeleton, Progress } from "@mantine/core";
import { STATUSES } from "@sdflc/utils";

import {
  AppIcon,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  CardListSkeleton,
  EmptyState,
  LoadMoreButton,
} from "@/components/generic";
import { PartnerKindBadge, PartnerKindIcon } from "@/components/features/EventPartners";
import { useAppEnvironment } from "@/contexts";
import { formatCurrency, formatNumber, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import {
  EventPartnerPackage,
  offeringAmount,
  offeringFeePassedOn,
  offeringTotalAmount,
  offeringCurrency,
  offeringSlotsLeft,
  offeringIsFull,
  offeringUsesDefaultPrice,
  partnerPaymentModeLabel,
} from "@/utils/api";

/**
 * The action key for the inline open/shut mutation.
 *
 * ⚠ Exported so the PAGE passes the same string to `update()` that this list reads back from
 * `isUpdating()`. Two literals would drift and the spinner would simply never appear — a silent
 * failure, because the row still updates.
 */
export const OFFERING_TOGGLE_ACTION = "toggle-open";

interface EventPartnerPackagesListProps {
  offerings: EventPartnerPackage[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEdit: (offering: EventPartnerPackage) => void;
  onRemove: (offering: EventPartnerPackage) => void;
  /** Flip the applications switch from the row — the most common single action on this page. */
  onToggleOpen: (offering: EventPartnerPackage) => void;
  /** Opens the add drawer from the first-run empty state. */
  onAdd?: () => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  isRemoving: (id?: string) => boolean;
  highlightedId?: string | null;
  /**
   * True when a search or a filter is narrowing the view.
   * ⚠ The list cannot see the page's search box, so this has to be a prop: without it an empty
   * result after a search renders "No packages offered yet", which tells a host their data is gone.
   */
  hasQueryNarrowing?: boolean;
}

/**
 * What it costs, saying plainly when the number came from the catalog rather than this event — and
 * when the sponsor will be charged MORE than it.
 *
 * ⚠ THE HOST NEEDS BOTH NUMBERS. The price they typed is what they are owed; under a `service` or
 * `service_processor` event the sponsor is charged that plus our fee. Showing only one of them
 * leaves the host guessing which one their sponsor is looking at.
 */
const PriceCell: React.FC<{ offering: EventPartnerPackage }> = ({ offering }) => {
  const { t } = useTranslation(["partners"]);
  const amount = offeringAmount(offering);
  const feePassedOn = offeringFeePassedOn(offering);

  if (amount === null) {
    return (
      <Text size="md" c="dimmed">
        {t("partners:offerings.price.free", { defaultValue: "Free" })}
      </Text>
    );
  }

  return (
    <Stack gap={0}>
      <Text size="md" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
        {formatCurrency(amount, offeringCurrency(offering) || undefined)}
      </Text>
      {offeringUsesDefaultPrice(offering) ? (
        <Text size="sm" c="dimmed">
          {t("partners:offerings.price.fromCatalog", { defaultValue: "catalog price" })}
        </Text>
      ) : null}
      {feePassedOn !== null ? (
        <Text size="sm" c="dimmed">
          {t("partners:offerings.price.sponsorPays", {
            defaultValue: "sponsor pays {{total}}",
            total: formatCurrency(offeringTotalAmount(offering) as number, offeringCurrency(offering) || undefined),
          })}
        </Text>
      ) : null}
    </Stack>
  );
};

/** Slots, as a number AND a bar — "2 of 3" is what a host reads, the bar is what they glance at. */
const SlotsCell: React.FC<{ offering: EventPartnerPackage }> = ({ offering }) => {
  const { t } = useTranslation(["partners"]);
  const left = offeringSlotsLeft(offering);
  const taken = Number(offering.slotsTaken || 0);

  if (left === null) {
    return (
      <Stack gap={2}>
        <Text size="md">{t("partners:offerings.slots.taken", { count: taken, defaultValue: "{{count}} taken" })}</Text>
        <Text size="sm" c="dimmed">
          {t("partners:offerings.slots.unlimited", { defaultValue: "unlimited" })}
        </Text>
      </Stack>
    );
  }

  const total = Number(offering.slotsTotal || 0);

  return (
    <Stack gap={4} style={{ minWidth: 90 }}>
      <Text size="md" style={{ fontVariantNumeric: "tabular-nums" }}>
        {t("partners:offerings.slots.ofTotal", {
          taken: formatNumber(taken),
          total: formatNumber(total),
          defaultValue: "{{taken}} of {{total}}",
        })}
      </Text>
      <Progress
        value={total > 0 ? Math.min(100, (taken / total) * 100) : 0}
        color={offeringIsFull(offering) ? "teal" : "blue"}
        size="sm"
        radius="sm"
      />
    </Stack>
  );
};

const StateBadges: React.FC<{ offering: EventPartnerPackage }> = ({ offering }) => {
  const { t } = useTranslation(["partners"]);

  return (
    <Group gap={6} wrap="wrap">
      <Badge size="md" variant="light" color={offering.isOpenForApplications ? "green" : "gray"}>
        {offering.isOpenForApplications
          ? t("partners:offerings.state.open", { defaultValue: "Open" })
          : t("partners:offerings.state.shut", { defaultValue: "Shut" })}
      </Badge>
      {offering.status !== STATUSES.ACTIVE ? (
        <Badge size="md" variant="light" color="yellow">
          {t("partners:offerings.state.draft", { defaultValue: "Draft" })}
        </Badge>
      ) : null}
      {offeringIsFull(offering) ? (
        <Badge size="md" variant="light" color="teal">
          {t("partners:offerings.state.full", { defaultValue: "Full" })}
        </Badge>
      ) : null}
    </Group>
  );
};

/**
 * The row actions — icon buttons, always visible, never hover-revealed.
 *
 * ⚠ Two different questions drive the two states. `isUpdating(id, ACTION)` drives THIS button's
 * spinner; `isUpdating(id)` (any action on the row) drives the siblings' disabled state. Keyed by
 * id alone, every button on the row would spin together.
 */
const RowActions: React.FC<{
  offering: EventPartnerPackage;
  onEdit: (o: EventPartnerPackage) => void;
  onRemove: (o: EventPartnerPackage) => void;
  onToggleOpen: (o: EventPartnerPackage) => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  isRemoving: (id?: string) => boolean;
}> = ({ offering, onEdit, onRemove, onToggleOpen, isUpdating, isRemoving }) => {
  const { t } = useTranslation(["partners", "common"]);
  const toggling = isUpdating(offering.id, OFFERING_TOGGLE_ACTION);
  const deleting = isRemoving(offering.id);
  const busy = isUpdating(offering.id) || deleting;

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      <Tooltip
        label={
          offering.isOpenForApplications
            ? t("partners:offerings.actions.shut", { defaultValue: "Shut applications" })
            : t("partners:offerings.actions.open", { defaultValue: "Open applications" })
        }
        withArrow
        position="bottom"
      >
        <ActionIcon
          variant="subtle"
          color={offering.isOpenForApplications ? "orange" : "green"}
          size="md"
          loading={toggling}
          disabled={busy && !toggling}
          onClick={(e) => {
            e.stopPropagation();
            onToggleOpen(offering);
          }}
        >
          <AppIcon iconName={offering.isOpenForApplications ? "Lock" : "LockOpen"} size={16} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
        <ActionIcon
          variant="subtle"
          color="blue"
          size="md"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(offering);
          }}
        >
          <AppIcon iconName="Edit" size={16} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
        <ActionIcon
          variant="subtle"
          color="red"
          size="md"
          loading={deleting}
          disabled={busy && !deleting}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(offering);
          }}
        >
          <AppIcon iconName="Trash" size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

/** ⚠ Matches the real row cell for cell, so the swap to data causes no layout shift. */
const OfferingTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td>
      <Skeleton height={16} width="60%" radius="sm" />
      <Skeleton height={12} width="35%" radius="sm" mt={6} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={80} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={70} radius="sm" />
      <Skeleton height={6} width={90} radius="sm" mt={6} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={120} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={20} width={60} radius="xl" />
    </Table.Td>
    <Table.Td>
      <Group gap={4} wrap="nowrap" justify="flex-end">
        <Skeleton height={28} circle />
        <Skeleton height={28} circle />
        <Skeleton height={28} circle />
      </Group>
    </Table.Td>
  </Table.Tr>
);

const OfferingCard: React.FC<{
  offering: EventPartnerPackage;
  highlighted: boolean;
  /** Tablet shows the buttons; a phone gets them from the swipe instead. */
  showActions: boolean;
  onEdit: (o: EventPartnerPackage) => void;
  onRemove: (o: EventPartnerPackage) => void;
  onToggleOpen: (o: EventPartnerPackage) => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  isRemoving: (id?: string) => boolean;
}> = ({ offering, highlighted, showActions, onEdit, onRemove, onToggleOpen, isUpdating, isRemoving }) => (
  <Paper
    withBorder
    radius="md"
    p="md"
    style={{ opacity: isRemoving(offering.id) ? 0.5 : 1, ...(highlighted ? HIGHLIGHT_STYLE : {}) }}
  >
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
          <PartnerKindIcon kind={offering.package?.partnerKind} size={34} />
        <Stack gap={3} style={{ minWidth: 0 }}>
          <Text size="md" fw={600} style={{ overflowWrap: "anywhere" }}>
            {offering.package?.name || "—"}
          </Text>
          <PartnerKindBadge kind={offering.package?.partnerKind} />
        </Stack>
        </Group>
        <StateBadges offering={offering} />
      </Group>

      <Group gap="xl" wrap="wrap">
        <PriceCell offering={offering} />
        <SlotsCell offering={offering} />
      </Group>

      <Group gap={6} wrap="nowrap">
        <AppIcon iconName="Coin" size={16} />
        <Text size="md">{partnerPaymentModeLabel(offering.paymentMode)}</Text>
      </Group>

      {showActions ? (
        <RowActions
          offering={offering}
          onEdit={onEdit}
          onRemove={onRemove}
          onToggleOpen={onToggleOpen}
          isUpdating={isUpdating}
          isRemoving={isRemoving}
        />
      ) : null}
    </Stack>
  </Paper>
);

const EventPartnerPackagesListComponent: React.FC<EventPartnerPackagesListProps> = ({
  offerings,
  loading,
  isLoaded,
  loadingMore,
  hasMore,
  onLoadMore,
  onEdit,
  onRemove,
  onToggleOpen,
  onAdd,
  isUpdating,
  isRemoving,
  highlightedId,
  hasQueryNarrowing,
}) => {
  const { t } = useTranslation(["partners", "common"]);
  const { isDesktop, isMobile } = useAppEnvironment();
  const { closeAllCards, shouldForceClose, handleForceCloseComplete, openCard } = useSwipeableList();

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  // ⚠ The empty branch runs only once the FIRST read is back, so the skeleton below wins until then.
  // Reversed, "No packages offered yet" flashes on every page open.
  if (!showSkeleton && offerings.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        {hasQueryNarrowing ? (
          <EmptyState
            title={t("partners:offerings.empty.noMatchTitle", { defaultValue: "No offerings match" })}
            description={t("partners:offerings.empty.noMatchDescription", {
              defaultValue: "Try a different search, or clear the filters.",
            })}
            iconName="Search"
            size="md"
          />
        ) : (
          <EmptyState
            title={t("partners:offerings.empty.title", { defaultValue: "No packages offered yet" })}
            description={t("partners:offerings.empty.description", {
              defaultValue: "Choose which of your sponsor, partner or exhibitor packages this event puts on the table.",
            })}
            iconName="Package"
            actionLabel={onAdd ? t("partners:offerings.actions.add", { defaultValue: "Offer a package" }) : undefined}
            onAction={onAdd}
            size="md"
          />
        )}
      </Paper>
    );
  }

  const loadMore = (
    <LoadMoreButton hasMore={!!hasMore} loading={!!loading} loadingMore={!!loadingMore} onLoadMore={onLoadMore} />
  );

  if (isDesktop) {
    return (
      <Stack gap="md">
        {/* ⚠ The table lives on its OWN white surface — a bordered Paper with the corners clipped,
            exactly as every other list page does it. Without it the rows sit straight on the grey
            page background and the page reads as unfinished next to its siblings. */}
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("partners:offerings.columns.package", { defaultValue: "Package" })}</Table.Th>
                <Table.Th style={{ width: 160 }}>{t("partners:offerings.columns.price", { defaultValue: "Price" })}</Table.Th>
                <Table.Th style={{ width: 150 }}>{t("partners:offerings.columns.slots", { defaultValue: "Slots" })}</Table.Th>
                <Table.Th style={{ width: 220 }}>{t("partners:offerings.columns.payment", { defaultValue: "Payment" })}</Table.Th>
                <Table.Th style={{ width: 170 }}>{t("partners:offerings.columns.state", { defaultValue: "State" })}</Table.Th>
                <Table.Th style={{ width: 130 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* ONE shell, two bodies — the skeleton cannot drift from the real row's columns. */}
              {showSkeleton
                ? [0, 1, 2].map((i) => <OfferingTableRowSkeleton key={i} />)
                : offerings.map((offering) => (
                    <Table.Tr
                      key={offering.id}
                      style={{
                        // ⚠ A <tr> cannot carry a ring or a shadow — HIGHLIGHT_STYLE on one renders a
                        // stray border. The row tint is its own token.
                        backgroundColor: offering.id === highlightedId ? HIGHLIGHT_ROW_BG : undefined,
                        opacity: isRemoving(offering.id) ? 0.5 : 1,
                        transition: "background-color 150ms ease, opacity 150ms ease",
                      }}
                    >
                      {/* ⚠ THE SAME LEADING ICON + TINTED BADGE the other two partner pages use.
                          This cell rendered a plain grey label, so a Gold Sponsor looked like a
                          different kind of thing depending on which of the three pages you were on —
                          exactly the drift PartnerKind was created to end. (The CARD branch of this
                          list already used them; only the table had been missed, which is why it
                          only showed on desktop.) */}
                      <Table.Td>
                        <Group gap="sm" wrap="nowrap">
                          <PartnerKindIcon kind={offering.package?.partnerKind} />
                          <Box style={{ minWidth: 0 }}>
                            <Text size="md" fw={600} lineClamp={1}>
                              {offering.package?.name || "—"}
                            </Text>
                            <Box mt={4}>
                              <PartnerKindBadge kind={offering.package?.partnerKind} size="xs" />
                            </Box>
                          </Box>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <PriceCell offering={offering} />
                      </Table.Td>
                      <Table.Td>
                        <SlotsCell offering={offering} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="md">{partnerPaymentModeLabel(offering.paymentMode)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <StateBadges offering={offering} />
                      </Table.Td>
                      <Table.Td>
                        <RowActions
                          offering={offering}
                          onEdit={onEdit}
                          onRemove={onRemove}
                          onToggleOpen={onToggleOpen}
                          isUpdating={isUpdating}
                          isRemoving={isRemoving}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return <CardListSkeleton rows={3} actions={3} actionsAt="bottom" leading={34} lines={5} lineSizes={['md', 'xs', 'md', 'sm', 'md']} lineGap={7} />;
  }

  // Tablet: cards WITH the buttons. No swipe — there is room, and a trackpad cannot swipe.
  if (!isMobile) {
    return (
      <Stack gap="md">
        <Stack gap="sm">
          {offerings.map((offering) => (
            <OfferingCard
              key={offering.id}
              offering={offering}
              highlighted={offering.id === highlightedId}
              showActions
              onEdit={onEdit}
              onRemove={onRemove}
              onToggleOpen={onToggleOpen}
              isUpdating={isUpdating}
              isRemoving={isRemoving}
            />
          ))}
        </Stack>
        {loadMore}
      </Stack>
    );
  }

  // Phone: the swipe IS the actions, so the card carries none of its own.
  // ⚠ Side convention — "do something" (open/shut) is marked secondary and lands on the LEFT;
  // edit and remove stay unmarked and route to the right.
  return (
    <Stack gap="md">
      <Stack gap="sm">
        {offerings.map((offering) => {
          const id = offering.id || "";
          const actions: SwipeAction[] = [
            {
              key: "toggle",
              label: offering.isOpenForApplications
                ? t("partners:offerings.state.shut", { defaultValue: "Shut" })
                : t("partners:offerings.state.open", { defaultValue: "Open" }),
              icon: offering.isOpenForApplications ? "Lock" : "LockOpen",
              color: offering.isOpenForApplications ? "orange" : "green",
              secondary: true,
              loading: isUpdating(id, OFFERING_TOGGLE_ACTION),
              onClick: () => {
                closeAllCards();
                onToggleOpen(offering);
              },
            },
            {
              key: "edit",
              label: t("common:buttons.edit"),
              icon: "Edit",
              color: "blue",
              onClick: () => {
                closeAllCards();
                onEdit(offering);
              },
            },
            {
              key: "remove",
              label: t("common:buttons.delete"),
              icon: "Trash",
              color: "red",
              loading: isRemoving(id),
              onClick: () => {
                closeAllCards();
                onRemove(offering);
              },
            },
          ];

          return (
            <SwipeableCard
              key={id}
              leftSwipeActions={actions}
              forceClose={shouldForceClose(id)}
              onForceCloseComplete={() => handleForceCloseComplete(id)}
              onSwipeStateChange={(isOpen) => {
                if (isOpen) {
                  openCard(id);
                }
              }}
              disabled={isRemoving(id)}
              borderRadius="var(--mantine-radius-md)"
            >
              <OfferingCard
                offering={offering}
                highlighted={offering.id === highlightedId}
                showActions={false}
                onEdit={onEdit}
                onRemove={onRemove}
                onToggleOpen={onToggleOpen}
                isUpdating={isUpdating}
                isRemoving={isRemoving}
              />
            </SwipeableCard>
          );
        })}
      </Stack>
      {loadMore}
    </Stack>
  );
};

// ⚠ Memoized like 31 of the 39 feature lists: the page re-renders on every keystroke in the header
// search box, and re-rendering a table of rows (each with three ActionIcons and a Progress bar) on
// each one is visible on a phone.
export const EventPartnerPackagesList = React.memo(EventPartnerPackagesListComponent);
